import { contactDisplayName } from '@taki/data-model';
import type { Asset, Booking, Contact } from '@taki/data-model';
import type { Option } from '@taki/ui';

/**
 * HELPERS METIER de l'app Reservations : catalogues d'options, lecture du sac
 * `customFields`, calculs de dates et de montants, mise en forme.
 *
 * Aucun JSX, aucun acces SDK : ce module est importe par l'ecran, par les
 * reglages et par le jeu de demonstration, et doit rester lisible sans rien
 * savoir de React ni de Firebase.
 *
 * UNE EXCEPTION ASSUMEE, en bas de fichier : l'ANNUAIRE VIVANT. Les bindings de
 * vues sont des constantes de MODULE (sinon la projection se recalcule a chaque
 * rendu, et pire : `useScreen` lit les bindings par reference, donc un binding
 * recree ne serait pris en compte qu'au prochain changement de donnees). Or le
 * sous-titre d'une location a besoin du NOM du client et le couloir du
 * calendrier a besoin des CATEGORIES du materiel loue, qui vivent dans deux
 * autres familles. L'ecran remplit donc cet annuaire dans son chargeur, AVANT
 * de rendre les locations : quand la projection tourne, l'annuaire est deja a
 * jour. C'est le meme geste que `demo-data.ts` de l'app ui-lab.
 */

/* ------------------------------------------------------------------ */
/* Le vocabulaire partage du pack (SPEC-COMMUNE, section 2)            */
/* ------------------------------------------------------------------ */

/** `type` local de l'app Reservations sur la famille `bookings`. */
export const BOOKING_TYPE = 'location';

/** `type` local du parc, pose par l'app Inventaire sur la famille `assets`. */
export const MATERIEL_TYPE = 'materiel';

/** Statut d'un materiel disponible a la location (catalogue de l'app Inventaire). */
export const MATERIEL_AVAILABLE = 'disponible';

/** Statut d'un materiel physiquement dehors, sorti en location. */
export const MATERIEL_RENTED = 'loue';

/**
 * LE GARDE-FOU DU PARC : les deux SEULS statuts entre lesquels cette app a le
 * droit de faire basculer un materiel.
 *
 * `maintenance` et `reforme` sont la verite de l'atelier et du parc, pas celle
 * du planning. Un materiel qui porte l'un des deux n'est JAMAIS touche, dans
 * aucun sens : sinon un simple retour de location effacerait le fait qu'une
 * camera est cassee a l'atelier, et l'app Maintenance perdrait son sujet.
 */
export const MATERIEL_SWITCHABLE: readonly string[] = [MATERIEL_AVAILABLE, MATERIEL_RENTED];

/** Catalogue FIGE des statuts d'une location. */
export type BookingStatus = 'option' | 'confirme' | 'sorti' | 'retourne' | 'annule';

/**
 * Les statuts, resolus au runtime par `options()` des roles `status` et
 * `groupKey`. Le `tone` est le SEUL vocabulaire de couleur partage : jamais un
 * hex, jamais une classe.
 */
export const BOOKING_STATUS_OPTIONS: Option[] = [
  { value: 'option', label: 'Option', tone: 'neutral' },
  { value: 'confirme', label: 'Confirmee', tone: 'accent' },
  { value: 'sorti', label: 'Sortie', tone: 'success' },
  { value: 'retourne', label: 'Retournee', tone: 'neutral' },
  { value: 'annule', label: 'Annulee', tone: 'danger' },
];

/** Statuts proposes a la CREATION (une location nait toujours en option ou confirmee). */
export const INITIAL_STATUS_VALUES: BookingStatus[] = ['option', 'confirme'];

/**
 * LE PARC SUIT LA LOCATION : le statut que doivent prendre les materiels
 * engages quand la location passe a `status`, ou `null` quand le parc ne bouge
 * pas. C'est la regle metier, pure ; l'ecriture, elle, vit dans l'ecran.
 *
 *  - `sorti` -> `loue` : le materiel est physiquement dehors, il ne peut plus
 *    etre reloue a quelqu'un d'autre ;
 *  - `retourne` ou `annule` -> `disponible` : il est de nouveau louable ;
 *  - `option` et `confirme` -> rien. On ne bloque pas un exemplaire au magasin
 *    tant qu'il n'est pas sorti : trois clients peuvent poser une option sur
 *    les memes dates, c'est le metier qui veut ca.
 *
 * Le parametre est une chaine BRUTE : le kanban livre la valeur de la colonne
 * d'accueil. Un statut inconnu ne declenche rien, plutot que d'inventer un
 * mouvement de parc.
 */
export function parcStatusFor(status: string): string | null {
  switch (status) {
    case 'sorti':
      return MATERIEL_RENTED;
    case 'retourne':
    case 'annule':
      return MATERIEL_AVAILABLE;
    default:
      return null;
  }
}

/**
 * Les 5 categories de materiel du pack : elles alimentent le role `lane` du
 * calendrier (une location de camera + lumiere occupe DEUX couloirs).
 */
export const CATEGORY_OPTIONS: Option[] = [
  { value: 'camera', label: 'Camera', tone: 'accent' },
  { value: 'optique', label: 'Optique', tone: 'neutral' },
  { value: 'lumiere', label: 'Lumiere', tone: 'success' },
  { value: 'son', label: 'Son', tone: 'neutral' },
  { value: 'machinerie', label: 'Machinerie', tone: 'neutral' },
];

/** Canal d'origine d'une location (champ natif `source` de la famille booking). */
export const SOURCE_OPTIONS: Option[] = [
  { value: 'telephone', label: 'Telephone', tone: 'neutral' },
  { value: 'email', label: 'Email', tone: 'neutral' },
  { value: 'comptoir', label: 'Comptoir', tone: 'neutral' },
];

/** Libelle affiche d'une valeur de catalogue ; la valeur brute si elle est inconnue. */
export function labelOf(catalog: Option[], value: string | undefined): string {
  if (value == null || value === '') return '';
  return catalog.find((option) => option.value === value)?.label ?? value;
}

/* ------------------------------------------------------------------ */
/* Lecture du sac customFields                                         */
/* ------------------------------------------------------------------ */

/** Une valeur de `customFields` lue en texte, ou `''` si absente ou d'un autre type. */
function readText(fields: Record<string, unknown> | undefined, key: string): string {
  const value = fields?.[key];
  return typeof value === 'string' ? value : '';
}

/** Une valeur de `customFields` lue en nombre, ou 0 si absente ou d'un autre type. */
function readNumber(fields: Record<string, unknown> | undefined, key: string): number {
  const value = fields?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Une valeur de `customFields` lue en liste de chaines, vide par defaut. */
function readList(fields: Record<string, unknown> | undefined, key: string): string[] {
  const value = fields?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/* ---- Accesseurs d'une location ---- */

/** Nom du projet de tournage : c'est le TITRE de la location dans toutes les vues. */
export function projectNameOf(booking: Booking): string {
  return readText(booking.customFields, 'projectName');
}

/** Personne jointe sur le tournage (`customFields.crewContact`). */
export function crewContactOf(booking: Booking): string {
  return readText(booking.customFields, 'crewContact');
}

/** Acompte verse, en euros. */
export function depositOf(booking: Booking): number {
  return readNumber(booking.customFields, 'deposit');
}

/** Montant total de la location, en euros. */
export function totalAmountOf(booking: Booking): number {
  return readNumber(booking.customFields, 'totalAmount');
}

/** Ids REELS des materiels loues (resolus depuis la famille `assets`). */
export function assetIdsOf(booking: Booking): string[] {
  return readList(booking.customFields, 'assetIds');
}

/** Consigne de comptoir libre. */
export function pickupNoteOf(booking: Booking): string {
  return readText(booking.customFields, 'pickupNote');
}

/** Cle du jeu de demonstration (idempotence de l'import). */
export function demoKeyOf(record: { customFields?: Record<string, unknown> }): string {
  return readText(record.customFields, 'demoKey');
}

/** Statut d'une location, `option` par defaut (le champ natif est optionnel). */
export function statusOf(booking: Booking): BookingStatus {
  const raw = booking.status ?? 'option';
  return BOOKING_STATUS_OPTIONS.some((option) => option.value === raw)
    ? (raw as BookingStatus)
    : 'option';
}

/* ---- Accesseurs d'un materiel ---- */

/** Categorie d'un materiel (`customFields.category`). */
export function categoryOf(asset: Asset): string {
  return readText(asset.customFields, 'category');
}

/** Tarif journalier d'un materiel, en euros. */
export function dailyRateOf(asset: Asset): number {
  return readNumber(asset.customFields, 'dailyRate');
}

/** Marque et modele, pour lire un materiel dans une liste. */
export function assetLabelOf(asset: Asset): string {
  const brand = readText(asset.customFields, 'brand');
  const model = readText(asset.customFields, 'model');
  const detail = [brand, model].filter((part) => part !== '').join(' ');
  return detail === '' ? asset.name : `${asset.name} (${detail})`;
}

/* ------------------------------------------------------------------ */
/* Dates, durees, mise en forme                                        */
/* ------------------------------------------------------------------ */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Minuit local du jour d'une date. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Deux dates tombent-elles le MEME jour (heure locale du poste) ? */
export function isSameDay(a: Date | undefined, b: Date): boolean {
  if (a == null) return false;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

/**
 * Nombre de JOURS DE LOCATION factures : les jours de calendrier couverts,
 * bornes comprises. Du 12/08 au 16/08 = 5 jours, la convention du comptoir.
 */
export function rentalDays(start: Date | undefined, end: Date | undefined): number {
  if (start == null || end == null) return 1;
  const span = Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / DAY_MS);
  return Math.max(1, span + 1);
}

/** Une date en "12/08", la forme courte du planning. */
export function formatDayMonth(date: Date | undefined): string {
  if (date == null) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/**
 * Une date-heure complete, "12/08/2026 a 09:00" : la forme de la FICHE. L'annee
 * y est, contrairement au sous-titre de la liste : une fiche se lit aussi sur
 * une location de l'an dernier, et l'heure d'une sortie de materiel compte.
 */
export function formatDateTime(date: Date | undefined): string {
  if (date == null) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${formatDayMonth(date)}/${date.getFullYear()} a ${hours}:${minutes}`;
}

/** Un montant en euros, formate a la francaise ("3 480 EUR"). */
export function formatEuros(amount: number): string {
  return `${new Intl.NumberFormat('fr-FR').format(Math.round(amount))} EUR`;
}

/** "1 materiel" / "4 materiels" : le pluriel se dit, il ne se devine pas. */
export function materialCountLabel(count: number): string {
  return count <= 1 ? `${count} materiel` : `${count} materiels`;
}

/** Une date en "YYYY-MM-DD" pour un `<input type="date">` (heure locale). */
export function toDayInput(date: Date | undefined): string {
  if (date == null) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Une date en "HH:MM" pour un `<input type="time">` (heure locale). */
export function toTimeInput(date: Date | undefined): string {
  if (date == null) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Recompose une date-heure LOCALE depuis les deux champs du formulaire.
 * Rend `null` si le jour manque : on ne fabrique jamais une date approximative.
 */
export function fromDayAndTime(day: string, time: string): Date | null {
  if (day === '') return null;
  const [year, month, date] = day.split('-').map((part) => Number(part));
  if (year == null || month == null || date == null) return null;
  const [hours, minutes] = (time === '' ? '00:00' : time).split(':').map((part) => Number(part));
  const built = new Date(year, month - 1, date, hours ?? 0, minutes ?? 0, 0, 0);
  return Number.isNaN(built.getTime()) ? null : built;
}

/** Ajoute des jours a une date, sans toucher a l'heure. */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/* ------------------------------------------------------------------ */
/* Les questions du comptoir                                           */
/* ------------------------------------------------------------------ */

/** La location est SORTIE et sa date de retour est passee : le materiel manque. */
export function isOverdue(booking: Booking, now: Date): boolean {
  if (statusOf(booking) !== 'sorti') return false;
  return booking.endAt != null && booking.endAt.getTime() < now.getTime();
}

/** Un depart est prevu aujourd'hui. */
export function leavesToday(booking: Booking, now: Date): boolean {
  return isSameDay(booking.startAt, now);
}

/** Un retour est prevu aujourd'hui. */
export function returnsToday(booking: Booking, now: Date): boolean {
  return isSameDay(booking.endAt, now);
}

/** Les trois sections du menu de la zone 1 : elles decoupent la MEME collection. */
export type SectionId = 'planning' | 'aujourdhui' | 'retard';

/**
 * Le predicat d'une section. Il sert DEUX fois : dans le chargeur (ce que la
 * zone 5 montre) et pour les compteurs du menu (calcules sur le jeu COMPLET).
 */
export function matchSection(booking: Booking, section: SectionId, now: Date): boolean {
  const status = statusOf(booking);
  switch (section) {
    case 'planning':
      return status !== 'annule';
    case 'aujourdhui':
      return status !== 'annule' && (leavesToday(booking, now) || returnsToday(booking, now));
    case 'retard':
      return isOverdue(booking, now);
    default:
      return true;
  }
}

/* ------------------------------------------------------------------ */
/* L'ANNUAIRE VIVANT : clients et materiels deja charges               */
/* ------------------------------------------------------------------ */

let contactsById = new Map<string, Contact>();
let materielById = new Map<string, Asset>();

/**
 * Remplit l'annuaire depuis les enregistrements DEJA charges par l'ecran.
 * Aucune requete ici : le module ne connait pas le SDK, il ne fait que ranger.
 */
export function setDirectory(contacts: Contact[], materiel: Asset[]): void {
  contactsById = new Map(contacts.map((contact) => [contact.id, contact]));
  materielById = new Map(materiel.map((asset) => [asset.id, asset]));
}

/** Le nom affichable d'un client. Le helper du socle gere personne ET organisation. */
export function contactNameOf(contactId: string | undefined): string {
  if (contactId == null || contactId === '') return '';
  const contact = contactsById.get(contactId);
  if (contact == null) return '';
  return contactDisplayName(contact);
}

/** Le client d'une location, ou un texte d'absence honnete. */
export function clientNameOf(booking: Booking): string {
  const name = contactNameOf(booking.contactId);
  return name === '' ? 'Client non renseigne' : name;
}

/** Initiales du client, pour la pastille de tete de ligne ("Prod Lumen" -> "PL"). */
export function clientInitialsOf(booking: Booking): string {
  const name = contactNameOf(booking.contactId);
  if (name === '') return '?';
  const letters = name
    .split(/\s+/)
    .filter((word) => word !== '')
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
  return letters === '' ? '?' : letters;
}

/** Les materiels d'une location, resolus dans l'annuaire (les inconnus sont ignores). */
export function materielOf(booking: Booking): Asset[] {
  return assetIdsOf(booking)
    .map((id) => materielById.get(id))
    .filter((asset): asset is Asset => asset != null);
}

/**
 * Les CATEGORIES engagees par une location, sans doublon et dans l'ordre du
 * catalogue. C'est le role `lane` MULTI-VALUE du calendrier : une location de
 * camera et de lumiere occupe deux couloirs.
 */
export function categoriesOf(booking: Booking): string[] {
  const found = new Set(materielOf(booking).map((asset) => categoryOf(asset)));
  const ordered = CATEGORY_OPTIONS.filter((option) => found.has(option.value)).map(
    (option) => option.value,
  );
  return ordered;
}

/**
 * Les memes categories, en toutes lettres ("Camera, Lumiere") : c'est ce que la
 * FICHE affiche. Le role `lane` ne convient pas la-bas, la fiche du socle le
 * nomme "Couloir" (le `label` d'un `FieldMap` n'arrive jamais jusqu'a la vue) ;
 * une colonne, elle, porte le mot du comptoir.
 */
export function categoryLabelsOf(booking: Booking): string {
  return categoriesOf(booking)
    .map((value) => labelOf(CATEGORY_OPTIONS, value))
    .filter((label) => label !== '')
    .join(', ');
}

/** La liste lisible du materiel loue, pour la fiche. */
export function materielLabelsOf(booking: Booking): string {
  const assets = materielOf(booking);
  if (assets.length === 0) return '';
  return assets.map((asset) => assetLabelOf(asset)).join(', ');
}

/** Somme des tarifs journaliers d'une selection de materiels, en euros. */
export function dailyTotalOf(assets: Asset[]): number {
  return assets.reduce((sum, asset) => sum + dailyRateOf(asset), 0);
}

/**
 * Le sous-titre d'une location : ce qu'un loueur veut lire d'un coup d'oeil.
 * "En retard" passe DEVANT le reste : c'est l'information qui coute de l'argent.
 */
export function subtitleOf(booking: Booking, now: Date): string {
  const parts: string[] = [];
  if (isOverdue(booking, now)) parts.push('En retard');
  parts.push(clientNameOf(booking));
  if (booking.startAt != null && booking.endAt != null) {
    parts.push(`du ${formatDayMonth(booking.startAt)} au ${formatDayMonth(booking.endAt)}`);
  } else {
    parts.push('dates non renseignees');
  }
  parts.push(materialCountLabel(assetIdsOf(booking).length));
  return parts.join(' · ');
}
